import React, { useState, useRef, useEffect, ChangeEvent } from "react";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from "primereact/confirmdialog";
import { LocationPhoto } from "@types";
import {
  useUploadLocationPhoto,
  useDeleteLocationPhoto,
  useSetLocationMainPhoto,
  useUpdateHomePhoto,
} from "@api";
import { useToast } from "@/providers";
import { LocationPhotoGalleryProps } from "./types";
import { STRING } from "@constants";
import { PhotoCard } from "@/components";

// Constants

// Photo card component

export function LocationPhotoGallery({
  locationId,
  photos = [],
  currentMainPhotoUrl,
  onPhotosUpdate,
  onMainPhotoChange,
  maxPhotos = 10,
}: LocationPhotoGalleryProps) {
  const [localPhotos, setLocalPhotos] = useState<LocationPhoto[]>(photos);
  const { triggerUploadPhoto, isLoading: isUploading } =
    useUploadLocationPhoto();
  const { triggerDeletePhoto, isLoading: isDeleting } =
    useDeleteLocationPhoto();
  const { triggerSetMainPhoto, isLoading: isSettingMain } =
    useSetLocationMainPhoto();
  const { triggerUpdateHomePhoto, isLoading: isUpdatingHome } =
    useUpdateHomePhoto();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isLoading =
    isUploading || isDeleting || isSettingMain || isUpdatingHome;

  useEffect(() => {
    setLocalPhotos(photos);
  }, [photos]);

  const showSuccessToast = (message: string) =>
    showToast({
      severity: "success",
      summary: "Success",
      detail: message,
      life: 3000,
    });
  const showErrorToast = (message: string) =>
    showToast({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showErrorToast(STRING.LOCATION_FORM.INVALID_FILE);
      return;
    }

    triggerUploadPhoto(
      { id: locationId, file },
      {
        onSuccess: (data) => {
          const updatedPhotos = data.photos;
          setLocalPhotos(updatedPhotos);
          onPhotosUpdate?.(updatedPhotos);

          if (localPhotos.length === 0 && updatedPhotos.length > 0) {
            const firstPhoto = updatedPhotos.find((p) => p.isMain);
            if (firstPhoto) {
              triggerUpdateHomePhoto(
                { id: locationId, photoUrl: firstPhoto.url },
                {
                  onSuccess: () => {
                    onMainPhotoChange?.(firstPhoto.url);
                    showSuccessToast(STRING.LOCATION_FORM.UPLOAD_FIRST_SUCCESS);
                  },
                },
              );
            }
          } else {
            showSuccessToast(STRING.LOCATION_FORM.UPLOAD_SUCCESS);
          }

          if (fileInputRef.current) fileInputRef.current.value = "";
        },
        onError: () => showErrorToast(STRING.LOCATION_FORM.UPLOAD_ERROR),
      },
    );
  };

  const handleDeletePhoto = (photoUrl: string) => {
    confirmDialog({
      message: STRING.LOCATION_FORM.DELETE_CONFIRM,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        triggerDeletePhoto(
          { id: locationId, photoUrl },
          {
            onSuccess: (data) => {
              const updatedPhotos = data.photos;
              setLocalPhotos(updatedPhotos);
              onPhotosUpdate?.(updatedPhotos);
              showSuccessToast(STRING.LOCATION_FORM.DELETE_SUCCESS);
            },
            onError: () => showErrorToast(STRING.LOCATION_FORM.DELETE_ERROR),
          },
        );
      },
    });
  };

  const handleSetMainPhoto = (photoUrl: string) => {
    triggerUpdateHomePhoto(
      { id: locationId, photoUrl },
      {
        onSuccess: () => {
          onMainPhotoChange?.(photoUrl);
          triggerSetMainPhoto(
            { id: locationId, photoUrl },
            {
              onSuccess: (data) => {
                const updatedPhotos = data.photos;
                setLocalPhotos(updatedPhotos);
                onPhotosUpdate?.(updatedPhotos);
                showSuccessToast(STRING.LOCATION_FORM.SET_MAIN_SUCCESS);
              },
            },
          );
        },
        onError: () => showErrorToast(STRING.LOCATION_FORM.SET_MAIN_ERROR),
      },
    );
  };

  const galleryPhotos = localPhotos.filter(
    (p) => p.url !== currentMainPhotoUrl,
  );
  const mainPhoto = localPhotos.find((p) => p.url === currentMainPhotoUrl);

  return (
    <div className="p-3">
      <div className="surface-50 p-3 border-round mb-3">
        <div className="flex justify-content-between align-items-center">
          <div>
            <h4 className="m-0 mb-1 text-800">Gallery Management</h4>
            <p className="text-sm text-600 m-0 mb-2">
              Showcase different views and features of this location
            </p>
            <div className="flex align-items-center gap-2">
              <i className="pi pi-images text-primary" />
              <span className="text-sm font-semibold text-primary">
                {localPhotos.length} / {maxPhotos} photos
              </span>
              {localPhotos.length >= maxPhotos && (
                <span className="text-xs text-orange-600 ml-2">
                  <i className="pi pi-exclamation-circle mr-1" />
                  Limit reached
                </span>
              )}
            </div>
          </div>
          {localPhotos.length < maxPhotos && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                disabled={isLoading}
              />
              <Button
                label="Upload Photo"
                icon="pi pi-cloud-upload"
                className="p-button-primary"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={isLoading}
                loading={isUploading}
              />
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      )}

      {!isLoading && localPhotos.length === 0 && (
        <div className="text-center p-5 surface-100 border-round border-2 border-dashed surface-border">
          <i className="pi pi-images text-4xl text-400 mb-3" />
          <h5 className="mb-2 text-700">No gallery photos yet</h5>
          <p className="text-sm text-600 m-0">
            Upload photos to showcase this location
          </p>
        </div>
      )}

      {!isLoading && localPhotos.length > 0 && (
        <div>
          {mainPhoto && (
            <PhotoCard
              photo={mainPhoto}
              index={0}
              isMain={true}
              isFeatured={false}
              onSetMain={() => {}}
              onDelete={() => handleDeletePhoto(mainPhoto.url)}
              isLoading={isLoading}
            />
          )}

          {galleryPhotos.length > 0 && (
            <div>
              <div className="flex align-items-center justify-content-between mb-3">
                <div>
                  <h5 className="m-0 text-900 font-semibold">Gallery Photos</h5>
                  <p className="text-sm text-600 m-0 mt-1">
                    Select any photo to set as main location image
                  </p>
                </div>
                <div className="bg-surface-100 px-2 py-1 border-round">
                  <span className="text-xs font-semibold text-700">
                    {galleryPhotos.length} photos
                  </span>
                </div>
              </div>
              <div className="grid">
                {galleryPhotos.map((photo, index) => (
                  <PhotoCard
                    key={photo.url}
                    photo={photo}
                    index={index}
                    isMain={false}
                    isFeatured={photo.isMain}
                    onSetMain={() => handleSetMainPhoto(photo.url)}
                    onDelete={() => handleDeletePhoto(photo.url)}
                    isLoading={isLoading}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
